import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoProjectResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface UseTodoProjectsReturn {
  projects: TodoProjectResponse[];
  /** Fast lookup: projectId → project (for chips/badges). */
  projectById: Map<string, TodoProjectResponse>;
  loading: boolean;
  createProject: (input: { name: string; color?: string | null }) => Promise<TodoProjectResponse>;
  updateProject: (
    projectId: string,
    input: { name?: string; color?: string | null; sortOrder?: number },
  ) => Promise<TodoProjectResponse>;
  deleteProject: (projectId: string) => Promise<void>;
}

/**
 * Workspace(holder)-scoped projects, shared across the holder's lists. CRUD is
 * owner/admin server-side; any member can read (for the picker + group-by view).
 * Assigning a project to a task rides the item-update path, not this hook.
 */
export function useTodoProjects(holderId: string | null): UseTodoProjectsReturn {
  const queryClient = useQueryClient();
  const key = queryKeys.todoLists.projects(holderId ?? '');
  const { toast } = useToast();

  const onError = () => toast({ title: 'Something went wrong', variant: 'destructive' });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.listTodoProjects(holderId as string),
    enabled: !!holderId,
  });

  const projects = data?.projects ?? [];
  const projectById = useMemo(() => new Map(projects.map((p) => [p.projectId, p])), [projects]);

  const createMut = useMutation({
    mutationFn: (input: { name: string; color?: string | null }) =>
      api.createTodoProject(holderId as string, input).then((r) => r.project),
    onSuccess: invalidate,
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (vars: {
      projectId: string;
      input: { name?: string; color?: string | null; sortOrder?: number };
    }) =>
      api.updateTodoProject(holderId as string, vars.projectId, vars.input).then((r) => r.project),
    onSuccess: invalidate,
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (projectId: string) => api.deleteTodoProject(holderId as string, projectId),
    // Deleting un-assigns tasks server-side; refresh the whole list detail so the
    // now-project-less tasks re-render.
    onSuccess: () => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.all });
    },
    onError,
  });

  return {
    projects,
    projectById,
    loading: isLoading,
    createProject: (input) => createMut.mutateAsync(input),
    updateProject: (projectId, input) => updateMut.mutateAsync({ projectId, input }),
    deleteProject: (projectId) => deleteMut.mutateAsync(projectId),
  };
}
